import { isFieldRequired } from './answers';
import {
  isQuestionVisible,
  isSectionVisible,
  type AnswerMap,
  type Question,
  type SampleEntryValue,
  type TemplateDefinition,
  type YesNoNa,
} from './template';

/**
 * Fill every visible, unanswered question with a plausible random answer,
 * producing a submittable answer set. Testing utility today (Admin-only
 * button on the conduct/preview surfaces); intended to back the Template
 * Builder's "preview with sample data" in Phase 5.
 *
 * Guarantees:
 * - Existing answers (including store-attribute gate pre-fills) are kept.
 * - Visibility is evaluated progressively in document order, so gated
 *   content is filled only when its gate landed on "yes" (validation
 *   guarantees gates are declared before what they gate).
 * - Conditionally-required sample fields (requiredIf) are filled exactly
 *   when required, exercising that logic.
 */
export function fillRandomAnswers(
  def: TemplateDefinition,
  existing: AnswerMap = {},
): AnswerMap {
  const answers: AnswerMap = { ...existing };

  for (const section of def.sections) {
    if (!isSectionVisible(def, section, answers)) continue;
    for (const q of section.questions) {
      if (!isQuestionVisible(def, section, q, answers)) continue;
      if (answers[q.id] !== undefined) continue;
      const value = randomAnswerFor(q);
      if (value !== undefined) answers[q.id] = value;
    }
  }
  return answers;
}

function randomAnswerFor(q: Question): AnswerMap[string] {
  switch (q.type) {
    case 'yes_no':
      return randomYesNo(false);
    case 'yes_no_na':
      return randomYesNo(true);
    case 'short_text':
      return randomShortText(q.text);
    case 'long_text':
      return 'Automated test fill — no findings noted.';
    case 'number':
      return randomInt(0, 20);
    case 'percent':
      return randomInt(40, 100);
    case 'date':
      return randomRecentDate();
    case 'multi_select': {
      const options = q.options ?? [];
      const selected = options.filter(() => Math.random() < 0.5);
      // Required multi_selects need at least one choice.
      return selected.length > 0 ? selected : options.slice(0, 1);
    }
    case 'sample_group': {
      const max = q.sampleCount ?? 1;
      const min = q.minCount ?? 0;
      const count = clamp(1 + randomInt(0, 2), Math.max(min, 1), max);
      const entries: SampleEntryValue[] = [];
      for (let i = 0; i < count; i++) {
        const entry: SampleEntryValue = {};
        // Yes/no fields first — requiredIf conditions depend on them.
        for (const field of q.fields ?? []) {
          if (field.type === 'yes_no' || field.type === 'yes_no_na') {
            entry[field.id] = randomYesNo(field.type === 'yes_no_na');
          }
        }
        for (const field of q.fields ?? []) {
          if (field.type === 'yes_no' || field.type === 'yes_no_na') continue;
          // Fill unconditionally-required always; conditional ones only when
          // their condition holds (exercises the requiredIf path).
          if (!field.requiredIf || isFieldRequired(field, entry)) {
            entry[field.id] = randomShortText(field.label);
          }
        }
        entries.push(entry);
      }
      return entries;
    }
  }
}

function randomYesNo(allowNa: boolean): YesNoNa {
  const r = Math.random();
  if (allowNa && r > 0.92) return 'na';
  return r < 0.75 ? 'yes' : 'no'; // mostly passing → realistic scores
}

const NAMES = ['J. Smith', 'M. Garcia', 'T. Nguyen', 'A. Johnson', 'R. Patel'];

function randomShortText(label: string): string {
  const lower = label.toLowerCase();
  if (lower.includes('unit')) return String(randomInt(100, 999));
  if (lower.includes('customer') || lower.includes('name')) return pick(NAMES);
  if (lower.includes('date')) return randomRecentDate();
  if (lower.includes('day')) return String(randomInt(1, 90));
  if (lower.includes('discrepan')) return 'Test discrepancy noted';
  if (lower.includes('item')) return `SKU-${randomInt(1000, 9999)}`;
  return `Test — ${randomInt(100, 999)}`;
}

function randomRecentDate(): string {
  const daysAgo = randomInt(0, 364);
  const d = new Date(Date.now() - daysAgo * 86_400_000);
  return d.toISOString().slice(0, 10);
}

function randomInt(min: number, max: number): number {
  return min + Math.floor(Math.random() * (max - min + 1));
}

function clamp(n: number, min: number, max: number): number {
  return Math.min(Math.max(n, min), max);
}

function pick<T>(arr: readonly T[]): T {
  return arr[Math.floor(Math.random() * arr.length)]!;
}
